/**
 * FieldHelpAside — the Field Help panel of a `/new` page (entity-management §2.3). Demo page: /form-layouts (form card
 * with a side card) on `FormPage`'s `aside`. It follows the field the person is working in and falls back to an
 * overview of the entity. The text is the `help=` text of each field (`client/src/help/*-fields.ts`), so edits happen
 * once. The assistant is opened from the same card.
 */
import React, { useState } from 'react';
import { BookOpen } from 'lucide-react';
import { Card } from '../../../components/ui/index.js';
import { sentenceCase } from '../../../components/ui/FilterSelect.js';

const FIELD_ATTRIBUTE = 'data-help-field';

/** Wraps one field so focus inside it selects its help entry. Adds no box of its own. */
export const HelpField: React.FC<{ name: string; children: React.ReactNode }> = ({ name, children }) => (
  <div className="contents" {...{ [FIELD_ATTRIBUTE]: name }}>
    {children}
  </div>
);

/** The field help key the person is working in, read from `onFocus` events that bubble up to the form. */
export function useFocusedHelpField() {
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const onFocus = (event: React.FocusEvent<HTMLElement>) => {
    const holder = event.target.closest(`[${FIELD_ATTRIBUTE}]`);
    setFocusedField(holder?.getAttribute(FIELD_ATTRIBUTE) ?? null);
  };
  return { focusedField, onFocus };
}

interface FieldHelpAsideProps {
  /** `{field name: help text}` — the same map the fields' `help=` read. */
  helpMap: Record<string, string>;
  /** Field name → label shown in the panel; falls back to the sentence-cased key. */
  labels?: Record<string, string>;
  focusedField: string | null;
  overviewTitle: string;
  overviewBody: string;
  /** The "Ask assistant" control (an `EntityAssistantDrawer` with its `trigger`). */
  assistant: React.ReactNode;
}

export const FieldHelpAside: React.FC<FieldHelpAsideProps> = ({
  helpMap,
  labels,
  focusedField,
  overviewTitle,
  overviewBody,
  assistant,
}) => {
  const help = focusedField ? helpMap[focusedField] : undefined;
  const title = focusedField && help ? (labels?.[focusedField] ?? humanize(focusedField)) : overviewTitle;
  return (
    <Card title="Field help">
      <div aria-live="polite" className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium text-heading">
          <BookOpen size={15} className="shrink-0 text-muted" aria-hidden="true" />
          {title}
        </div>
        <p className="text-sm leading-relaxed whitespace-pre-wrap text-body">{help ?? overviewBody}</p>
        {!help && <p className="text-xs text-muted">Click into a field to see what it does.</p>}
      </div>
      <div className="mt-5 border-t pt-4">{assistant}</div>
    </Card>
  );
};

/** `permissionTier` → `Permission tier`. */
function humanize(key: string): string {
  return sentenceCase(key.replace(/([A-Z])/g, ' $1').toLowerCase());
}
